import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient, useTheme } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  GraduationCap,
  Plus,
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  PlayCircle,
  MoreVertical,
  Trash2,
  ChevronRight,
  Sun,
  Moon,
  Settings,
  LogOut,
  CreditCard,
  Sparkles,
  AlertCircle,
  Target,
  GitCompare,
  X,
  Download,
} from "lucide-react";
import { motion } from "framer-motion";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [colleges, setColleges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCollegeName, setNewCollegeName] = useState("");
  const [newCollegeType, setNewCollegeType] = useState("Regular Decision");
  const [newCollegeCategory, setNewCollegeCategory] = useState("target");
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // null = all, or "submitted", "in_progress", "not_started"
  
  // College search state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [collegesRes, statsRes] = await Promise.all([
        apiClient.get("/colleges"),
        apiClient.get("/dashboard/stats"),
      ]);
      setColleges(collegesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Search colleges from pre-populated database
  const searchColleges = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await apiClient.get(`/colleges/search?q=${encodeURIComponent(query)}&limit=8`);
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error("Error searching colleges:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchColleges(newCollegeName);
    }, 300);
    return () => clearTimeout(timer);
  }, [newCollegeName]);

  const selectCollege = (college) => {
    setNewCollegeName(college.name);
    setShowSearchResults(false);
    setSearchResults([]);
  };

  const handleAddCollege = async () => {
    if (!newCollegeName.trim()) {
      toast.error("Please enter a college name");
      return;
    }

    setSubmitting(true);
    setIsAddDialogOpen(false);
    setShowSearchResults(false);
    
    const collegeName = newCollegeName.trim();
    setNewCollegeName("");
    setNewCollegeType("Regular Decision");
    setNewCollegeCategory("target");
    
    // Show loading toast
    const toastId = toast.loading(`Adding ${collegeName} and fetching info...`);
    
    try {
      const response = await apiClient.post("/colleges", {
        name: collegeName,
        application_type: newCollegeType,
        category: newCollegeCategory,
      });
      
      // Auto-fetch AI info for the new college
      try {
        await apiClient.post(`/colleges/${response.data.college_id}/fetch-info`);
        toast.success(`${collegeName} added with all info!`, { id: toastId });
      } catch (aiError) {
        console.error("Error fetching AI info:", aiError);
        toast.success(`${collegeName} added! (Info fetch pending)`, { id: toastId });
      }
      
      await fetchData();
    } catch (error) {
      console.error("Error adding college:", error);
      toast.error("Failed to add college", { id: toastId });
      toast.error("Failed to add college");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    if (colleges.length === 0) {
      toast.error("No colleges to export");
      return;
    }
    const headers = ["Name", "Application Type", "Category", "Status", "Deadline", "Regular Decision", "Early Decision", "Early Action", "Priority Deadline", "FAFSA Deadline", "Transfer Fall", "Transfer Spring", "Tuition (In-State)", "Tuition (Out-of-State)", "Room & Board", "Acceptance Rate", "Ranking", "Location"];
    const rows = colleges.map((c) => [
      c.name || "",
      c.application_type || "",
      c.category || "",
      (c.status || "").replace("_", " "),
      c.deadline || "",
      c.regular_deadline || "",
      c.early_decision_deadline || "",
      c.early_action_deadline || "",
      c.priority_deadline || "",
      c.financial_aid_deadline || "",
      c.transfer_fall_deadline || "",
      c.transfer_spring_deadline || "",
      c.tuition_in_state || "",
      c.tuition_out_state || "",
      c.room_and_board || "",
      c.acceptance_rate || "",
      c.ranking || "",
      c.location || "",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`));
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `admit-list-colleges-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully!");
  };

  const handleDeleteCollege = async (collegeId, e) => {
    e.stopPropagation();
    try {
      await apiClient.delete(`/colleges/${collegeId}`);
      toast.success("College removed");
      await fetchData();
    } catch (error) {
      console.error("Error deleting college:", error);
      toast.error("Failed to delete college");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "submitted":
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "in_progress":
        return <PlayCircle className="w-4 h-4 text-violet-500" />;
      default:
        return <Circle className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: "status-badge-submitted",
      in_progress: "status-badge-in-progress",
      not_started: "status-badge-not-started",
    };
    const labels = {
      submitted: "Submitted",
      in_progress: "In Progress",
      not_started: "Not Started",
    };
    return (
      <span className={`status-badge ${styles[status] || styles.not_started}`}>
        {labels[status] || "Not Started"}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const config = {
      reach: { color: "bg-rose-500", label: "Reach" },
      target: { color: "bg-amber-500", label: "Target" },
      safety: { color: "bg-emerald-500", label: "Safety" },
    };
    const cat = config[category] || config.target;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className={`w-2 h-2 rounded-full ${cat.color}`} />
        {cat.label}
      </span>
    );
  };

  // Filter colleges based on selected status
  const filteredColleges = statusFilter
    ? colleges.filter((c) => c.status === statusFilter)
    : colleges;

  const handleStatClick = (filter) => {
    if (statusFilter === filter) {
      setStatusFilter(null); // Toggle off if same filter clicked
    } else {
      setStatusFilter(filter);
    }
  };

  const getFilterLabel = () => {
    switch (statusFilter) {
      case "submitted":
        return "Submitted";
      case "in_progress":
        return "In Progress";
      case "not_started":
        return "Not Started";
      default:
        return null;
    }
  };

  const getDaysUntilDeadline = (deadline) => {
    if (!deadline || deadline === "Rolling") return null;
    try {
      const deadlineDate = new Date(deadline);
      const today = new Date();
      const diffTime = deadlineDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const getDeadlineDisplay = (deadline) => {
    if (!deadline) return { text: "No deadline set", className: "text-muted-foreground" };
    if (deadline === "Rolling") return { text: "Rolling Admission", className: "text-muted-foreground" };
    
    const days = getDaysUntilDeadline(deadline);
    if (days === null) return { text: deadline, className: "text-muted-foreground" };
    
    if (days < 0) return { text: "Past deadline", className: "deadline-urgent" };
    if (days === 0) return { text: "Due today!", className: "deadline-urgent" };
    if (days <= 7) return { text: `${days} days left`, className: "deadline-urgent" };
    if (days <= 30) return { text: `${days} days left`, className: "deadline-soon" };
    return { text: `${days} days left`, className: "deadline-normal" };
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="font-bold text-xl font-outfit">Admit List</span>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              <Button
                variant="ghost"
                className="text-foreground"
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/calendar")}
                data-testid="nav-calendar-btn"
              >
                Calendar
              </Button>
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/categories")}
                data-testid="nav-categories-btn"
              >
                Categories
              </Button>
            </nav>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")} data-testid="subscription-menu-item">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-2">
              Welcome back, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-muted-foreground">
              Track your college applications and never miss a deadline.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={handleExportCSV}
              data-testid="export-csv-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Export CSV
            </Button>

            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate("/compare")}
              data-testid="compare-btn"
            >
              <GitCompare className="w-5 h-5 mr-2" />
              Compare
            </Button>

            <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
              setIsAddDialogOpen(open);
              if (!open) {
                setShowSearchResults(false);
                setSearchResults([]);
              }
            }}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-college-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add College
                </Button>
              </DialogTrigger>
            <DialogContent className="overflow-visible">
              <DialogHeader>
                <DialogTitle>Add a College</DialogTitle>
                <DialogDescription>
                  Search from 100+ colleges or enter a custom name. Our AI will fetch deadlines and requirements.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2 relative">
                  <Label htmlFor="college-name">College Name</Label>
                  <Input
                    id="college-name"
                    placeholder="Start typing to search..."
                    value={newCollegeName}
                    onChange={(e) => setNewCollegeName(e.target.value)}
                    onFocus={() => newCollegeName.length >= 2 && setShowSearchResults(true)}
                    autoComplete="off"
                    data-testid="college-name-input"
                  />
                  {/* Search Results Dropdown */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {searchResults.map((college, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors border-b border-border last:border-0"
                          onClick={() => selectCollege(college)}
                          data-testid={`search-result-${index}`}
                        >
                          <div className="font-medium">{college.name}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>{college.location}</span>
                            <span>•</span>
                            <span>{college.type}</span>
                            <span>•</span>
                            <span className="text-xs">{college.acceptance_rate} acceptance</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchLoading && (
                    <div className="absolute right-3 top-9">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="application-type">Application Type</Label>
                  <Select value={newCollegeType} onValueChange={setNewCollegeType}>
                    <SelectTrigger data-testid="application-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Early Decision">Early Decision</SelectItem>
                      <SelectItem value="Early Action">Early Action</SelectItem>
                      <SelectItem value="Regular Decision">Regular Decision</SelectItem>
                      <SelectItem value="Rolling">Rolling Admission</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="college-category">School Category</Label>
                  <Select value={newCollegeCategory} onValueChange={setNewCollegeCategory}>
                    <SelectTrigger data-testid="college-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reach">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-rose-500" />
                          Reach School
                        </div>
                      </SelectItem>
                      <SelectItem value="target">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500" />
                          Target School
                        </div>
                      </SelectItem>
                      <SelectItem value="safety">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          Safety School
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddCollege} disabled={submitting} data-testid="submit-college-btn">
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Add & Fetch Info
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className={`card-hover cursor-pointer transition-all ${statusFilter === null ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setStatusFilter(null)}
            data-testid="stat-total"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{stats?.total_colleges || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Schools</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`card-hover cursor-pointer transition-all ${statusFilter === 'submitted' ? 'ring-2 ring-emerald-500' : ''}`}
            onClick={() => handleStatClick('submitted')}
            data-testid="stat-submitted"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{stats?.submitted || 0}</p>
                  <p className="text-xs text-muted-foreground">Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`card-hover cursor-pointer transition-all ${statusFilter === 'in_progress' ? 'ring-2 ring-violet-500' : ''}`}
            onClick={() => handleStatClick('in_progress')}
            data-testid="stat-in-progress"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{stats?.in_progress || 0}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`card-hover cursor-pointer transition-all ${statusFilter === 'not_started' ? 'ring-2 ring-zinc-500' : ''}`}
            onClick={() => handleStatClick('not_started')}
            data-testid="stat-not-started"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                  <Circle className="w-5 h-5 text-zinc-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{stats?.not_started || 0}</p>
                  <p className="text-xs text-muted-foreground">Not Started</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Deadlines Alert */}
        {stats?.upcoming_deadlines?.length > 0 && !statusFilter && (
          <Card className="mb-8 border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-rose-700 dark:text-rose-400">
                <AlertCircle className="w-5 h-5" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.upcoming_deadlines.map((deadline) => (
                  <div
                    key={deadline.college_id}
                    className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-zinc-900 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800"
                    onClick={() => navigate(`/college/${deadline.college_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(deadline.status)}
                      <span className="font-medium">{deadline.name}</span>
                    </div>
                    <span className="font-mono text-sm text-rose-600 dark:text-rose-400">
                      {deadline.days_until === 0
                        ? "Today!"
                        : `${deadline.days_until} days`}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Colleges Grid */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold font-outfit">
              {statusFilter ? `${getFilterLabel()} Schools` : 'Your Colleges'}
            </h2>
            {statusFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter(null)}
                className="text-muted-foreground h-7 px-2"
              >
                <X className="w-4 h-4 mr-1" />
                Clear filter
              </Button>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {filteredColleges.length} {filteredColleges.length === 1 ? 'school' : 'schools'}
          </span>
        </div>

        {filteredColleges.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {statusFilter ? `No ${getFilterLabel().toLowerCase()} colleges` : 'No colleges added yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {statusFilter 
                  ? `You don't have any colleges marked as "${getFilterLabel()}".`
                  : 'Start by adding the colleges you\'re applying to.'}
              </p>
              {statusFilter ? (
                <Button onClick={() => setStatusFilter(null)} variant="outline" className="rounded-full">
                  Show All Colleges
                </Button>
              ) : (
                <Button onClick={() => setIsAddDialogOpen(true)} className="rounded-full" data-testid="empty-add-college-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Your First College
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="bento-grid">
            {filteredColleges.map((college, index) => {
              const deadlineInfo = getDeadlineDisplay(college.deadline);
              return (
                <motion.div
                  key={college.college_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card
                    className="card-hover cursor-pointer h-full"
                    onClick={() => navigate(`/college/${college.college_id}`)}
                    data-testid={`college-card-${college.college_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-1 font-outfit line-clamp-1">
                            {college.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">{college.application_type}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`college-menu-${college.college_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => handleDeleteCollege(college.college_id, e)}
                              className="text-destructive"
                              data-testid={`delete-college-${college.college_id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className={`text-sm font-mono ${deadlineInfo.className}`}>
                            {deadlineInfo.text}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          {getStatusBadge(college.status)}
                          <div className="flex items-center gap-2">
                            {getCategoryBadge(college.category)}
                            {college.ai_fetched && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Sparkles className="w-3 h-3" />
                                AI Info
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-end mt-4 pt-4 border-t border-border">
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          View Details
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2 text-primary"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/categories")}
            data-testid="mobile-nav-categories"
          >
            <Target className="w-5 h-5" />
            <span className="text-xs">Categories</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
